<script setup lang="ts">
/**
 * `/usage` — per-request log for the current hub user.
 *
 * Layout strategy:
 *   - Full-width content area (no `max-w-*`) per T04 spec.
 *   - Header on top: title + subtitle.
 *   - Filters card: native date inputs + Errors- only checkbox + Reset.
 *   - Table card: 8 columns (DATE / STATUS / MODEL / PROMPT / COMPLETION /
 *     TOTAL TOKENS / COST / DETAIL), latest requests first.
 *   - Pagination row: Previous / Next + "Showing X–Y of Z".
 *
 * Data source: `GET /api/usage?from=&to=&errors_ only=&page=&page_size=`
 * (T09 hub controller; row shape is the router's UsageRow from
 * src/services/usage-service.ts). Filters sync to URL query params so the
 * page is deep-linkable; updates use `router.replace` to avoid filling
 * browser history with filter tweaks.
 *
 * STATUS badge reuses the exact Active/Revoked pill classes from KeysView
 * (green 2xx / red 4xx-5xx). MODEL cell uses the muted text colour. COST
 * is formatted with up to 8 decimal places — small upstream rates need that
 * precision to stay readable (the router rounds to 6dp in SQL; 8dp here is
 * purely for human readability, not extra precision).
 */
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUsageStore } from '../stores/usage';
import { usageApi, type UsageRow } from '../api/usage';
import { formatDateTime } from '../lib/format';

const usage = useUsageStore();
const route = useRoute();
const router = useRouter();

// Local refs bound to the date inputs / checkbox (UI- facing copies).
const fromLocal = ref<string>(usage.from ?? '');
const toLocal = ref<string>(usage.to ?? '');
const errorsOnlyLocal = ref<boolean>(usage.errorsOnly);

// Errors- only count for the toggle badge.
const errorsTotal = ref<number | null>(null);

const rangeStart = computed(() => usage.total === 0 ? 0 : (usage.page - 1) * usage.pageSize + 1);
const rangeEnd = computed(() => usage.total === 0 ? 0 : Math.min(usage.page * usage.pageSize, usage.total));
const hasActiveFilters = computed(() => !!fromLocal.value || !!toLocal.value || errorsOnlyLocal.value);

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Normalize a query value to a YYYY- MM-DD string for the <input type="date"> binding. */
function coerceDate(q: unknown): string {
  if (typeof q !== 'string' || q.length < 10) return '';
  return q.slice(0, 10);
}

function coerceInt(q: unknown): number | null {
  if (typeof q !== 'string') return null;
  const n = parseInt(q, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function coerceBool(q: unknown): boolean {
  return q === 'true' || q === '1';
}

/** Build a clean URL query object — drop undefined keys to keep the URL tidy. */
function buildQuery(): Record<string, string> {
  const q: Record<string, string> = {};
  if (fromLocal.value) q.from = fromLocal.value;
  if (toLocal.value) q.to = toLocal.value;
  if (errorsOnlyLocal.value) q.errors_only = 'true';
  if (usage.page > 1) q.page = String(usage.page);
  if (usage.pageSize !== 50) q.page_size = String(usage.pageSize);
  return q;
}

async function syncUrl(): Promise<void> {
  await router.replace({ query: buildQuery() });
}

async function refreshSummaryCount(): Promise<void> {
  const since = fromLocal.value ? `${fromLocal.value}T00:00:00.000Z` : undefined;
  try {
    const res = await usageApi.summary(since ? { since } : {});
    errorsTotal.value = res.total_requests;
  } catch {
    errorsTotal.value = null;
  }
}

// ── Event handlers ─────────────────────────────────────────────────────────────

async function onFiltersChanged(): Promise<void> {
  await usage.setFilters({
    from: fromLocal.value || null,
    to: toLocal.value || null,
    errorsOnly: errorsOnlyLocal.value,
  });
  await Promise.all([syncUrl(), refreshSummaryCount()]);
}

async function onPrev(): Promise<void> {
  await usage.prevPage();
  await syncUrl();
}

async function onNext(): Promise<void> {
  await usage.nextPage();
  await syncUrl();
}

async function onReset(): Promise<void> {
  fromLocal.value = '';
  toLocal.value = '';
  errorsOnlyLocal.value = false;
  await usage.reset();
  await Promise.all([syncUrl(), refreshSummaryCount()]);
}

// ── STATUS badge helper ────────────────────────────────────────────────────────

type BadgeTone = 'ok' | 'danger' | 'muted';

function badgeFor(r: UsageRow): { tone: BadgeTone; label: string } {
  const s = r.http_status;
  if (s != null) {
    if (s >= 200 && s < 300) return { tone: 'ok', label: s === 200 ? '200 OK' : String(s) };
    if (s >= 500) return { tone: 'danger', label: `${s} upstream` };
    if (s >= 400) return { tone: 'danger', label: String(s) };
  }
  if (r.status === 'error') return { tone: 'danger', label: 'upstream' };
  return { tone: 'muted', label: '—' };
}

function pillClass(tone: BadgeTone): string {
  if (tone === 'ok') return 'inline-flex items-center text-[11px] font-medium tracking-wide uppercase px-2.5 py-1 rounded-full border bg-surface-soft border-ok/40 text-ok';
  if (tone === 'danger') return 'inline-flex items-center text-[11px] font-medium tracking-wide uppercase px-2.5 py-1 rounded-full border bg-surface-soft border-danger/30 text-danger';
  return 'inline-flex items-center text-[11px] font-medium tracking-wide uppercase px-2.5 py-1 rounded-full border bg-surface-soft border-hairline text-muted';
}

// ── USD formatter (local to this view) ─────────────────────────────────────────

function formatUsd8(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 8 });
}

// ── Lifecycle ──────────────────────────────────────────────────────────────────

onMounted(async () => {
  fromLocal.value = coerceDate(route.query.from) || usage.from || '';
  toLocal.value = coerceDate(route.query.to) || usage.to || '';
  errorsOnlyLocal.value = coerceBool(route.query.errors_only) || usage.errorsOnly;
  const initialPage = coerceInt(route.query.page) ?? usage.page;
  const initialPageSize = coerceInt(route.query.page_size) ?? usage.pageSize;

  await usage.load({
    page: initialPage,
    page_size: initialPageSize,
    from: fromLocal.value || null,
    to: toLocal.value || null,
    errorsOnly: errorsOnlyLocal.value,
  });
  await refreshSummaryCount();
});
</script>

<template>
  <div class="px-6 lg:px-10 py-8 flex flex-col gap-6">

    <!-- Header -->
    <div>
      <h1 class="text-2xl font-semibold text-ink">Usage</h1>
      <p class="text-sm text-muted mt-1">Every API request, including failed ones, so you can see exactly what happened.</p>
    </div>

    <!-- Error banner -->
    <div v-if="usage.error" class="card border-danger p-4 flex items-center justify-between gap-4">
      <span class="text-danger text-sm">{{ usage.error }}</span>
      <button class="btn-ghost text-xs" @click="usage.refresh()">Retry</button>
    </div>

    <!-- Filters card -->
    <div class="card p-4 flex items-center gap-3 flex-wrap">
      <span class="text-sm text-muted">From</span>
      <input type="date" v-model="fromLocal" class="input" @change="onFiltersChanged" />

      <span class="text-sm text-muted">To</span>
      <input type="date" v-model="toLocal" class="input" @change="onFiltersChanged" />

      <label class="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
        <input type="checkbox" v-model="errorsOnlyLocal" @change="onFiltersChanged" class="accent-accent" />
        Errors only
        <span v-if="errorsTotal != null" class="text-muted">({{ errorsTotal }})</span>
      </label>

      <button v-if="hasActiveFilters" class="btn-ghost text-xs ml-auto" @click="onReset">Reset</button>
    </div>

    <!-- Table card -->
    <div class="card overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-border">
            <th class="text-left text-xs font-medium text-muted uppercase tracking-wider px-4 py-3 w-[18%]">Date</th>
            <th class="text-left text-xs font-medium text-muted uppercase tracking-wider px-4 py-3 w-[11%]">Status</th>
            <th class="text-left text-xs font-medium text-muted uppercase tracking-wider px-4 py-3 w-[18%]">Model</th>
            <th class="text-right text-xs font-medium text-muted uppercase tracking-wider px-4 py-3 w-[9%]">Prompt</th>
            <th class="text-right text-xs font-medium text-muted uppercase tracking-wider px-4 py-3 w-[11%]">Completion</th>
            <th class="text-right text-xs font-medium text-muted uppercase tracking-wider px-4 py-3 w-[11%]">Total Tokens</th>
            <th class="text-right text-xs font-medium text-muted uppercase tracking-wider px-4 py-3 w-[14%]">Cost</th>
            <th class="text-left text-xs font-medium text-muted uppercase tracking-wider px-4 py-3">Detail</th>
          </tr>
        </thead>
        <tbody>
          <!-- Loading row -->
          <tr v-if="usage.loading && usage.rows.length === 0">
            <td colspan="8" class="text-muted text-center py-6">Loading…</td>
          </tr>
          <!-- Empty state -->
          <tr v-else-if="!usage.loading && usage.total === 0 && !usage.error">
            <td colspan="8" class="text-muted italic text-center py-6">No requests match the current filters.</td>
          </tr>
          <!-- Data rows -->
          <tr v-else v-for="r in usage.rows" :key="r.request_id ?? r.internal_req_id ?? Math.random()" class="border-b border-border/50 hover:bg-white/5">
            <td class="text-muted whitespace-nowrap px-4 py-3">{{ formatDateTime(r.timestamp) }}</td>
            <td class="px-4 py-3">
              <span :class="pillClass(badgeFor(r).tone)">{{ badgeFor(r).label }}</span>
            </td>
            <td class="px-4 py-3"><code class="font-mono text-xs text-muted">{{ r.model }}</code></td>
            <td class="text-right text-muted px-4 py-3">{{ r.prompt_tokens ?? '—' }}</td>
            <td class="text-right text-muted px-4 py-3">{{ r.completion_tokens ?? '—' }}</td>
            <td class="text-right text-muted px-4 py-3">{{ r.total_tokens ?? '—' }}</td>
            <td class="text-right px-4 py-3">{{ formatUsd8(r.cost_usd) }}</td>
            <td class="text-muted text-xs max-w-[260px] truncate px-4 py-3" :title="r.error_code ?? ''">{{ r.error_code ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination row -->
    <div class="flex justify-between items-center mt-4">
      <span class="text-xs text-muted">Showing {{ rangeStart }}–{{ rangeEnd }} of {{ usage.total }}</span>
      <div class="flex gap-2">
        <button class="btn-ghost text-xs" :disabled="!usage.hasPrev || usage.loading" @click="onPrev">Previous</button>
        <button class="btn-ghost text-xs" :disabled="!usage.hasNext || usage.loading" @click="onNext">Next</button>
      </div>
    </div>

  </div>
</template>